import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Prints a scannable QR that opens the ticket-raise form pre-scoped to a single outlet.
 * URL uses window.location.origin so admins printing from production get production URLs
 * and vice versa — no manual domain juggling.
 */
export default function OutletQRDialog({ outlet, open, onOpenChange }) {
  const [printing, setPrinting] = useState(false);
  if (!outlet) return null;
  const url = `${window.location.origin}/raise/${outlet.id}`;

  const handlePrint = () => {
    setPrinting(true);
    // Give the print-only DOM a moment to lay out then invoke the OS print dialog.
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 100);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-4 h-4" /> Scan-to-raise QR
          </DialogTitle>
        </DialogHeader>

        {/* Printable card — kept visually rich on screen; a global @media print rule below
            hides everything else so the printout is just this card. */}
        <div id="outlet-qr-print-region" className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Job Ticker</div>
          <div className="mt-1 font-display font-bold text-2xl text-slate-900" data-testid="qr-outlet-name">
            {outlet.name}
          </div>
          <div className="text-sm text-slate-500 mt-0.5">Outlet code · <span className="font-semibold text-slate-700">{outlet.code}</span></div>

          <div className="mt-4 grid place-items-center">
            <div className="rounded-xl bg-white border border-slate-200 p-3">
              <QRCodeSVG value={url} size={200} level="M" includeMargin={false} />
            </div>
          </div>

          <div className="mt-4 text-sm font-semibold text-slate-800">Scan to raise a ticket for this outlet</div>
          <div className="text-[11px] text-slate-500 mt-1">
            Point your camera at the QR. Log in once — your outlet is pre-selected every time.
          </div>
          <div className="text-[10px] text-slate-400 mt-3 break-all">{url}</div>
        </div>

        <div className="flex justify-end gap-2 print:hidden">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={handlePrint} disabled={printing} data-testid="print-qr-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
        </div>

        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            #outlet-qr-print-region, #outlet-qr-print-region * { visibility: visible !important; }
            #outlet-qr-print-region {
              position: fixed !important;
              inset: 0 !important;
              margin: auto !important;
              width: 90mm !important;
              height: auto !important;
              border: none !important;
              box-shadow: none !important;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
